"use client";

import React, { CSSProperties, useEffect, useRef, useState } from "react";
import { useTheme, useMediaQuery } from "@mui/material";
import { useRouter } from "next/navigation";

interface TrailCircle {
  id: number;
  targetX: number;
  targetY: number;
  currentX: number;
  currentY: number;
}
interface MobicomMascotProps {
  width?: string | number;
  height?: string | number;
  top?: string | number;
  bottom?: string | number;
  left?: string | number;
  right?: string | number;
  zIndex?: number;
  animate?: boolean;
  animationDuration?: number;
  clickable?: boolean;
  onClick?: () => void;
  className?: string;
  style?: CSSProperties;
  opacity?: number;
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
  draggable?: boolean;
  constrainToViewport?: boolean;
  onDragStart?: () => void;
  onDrag?: (x: number, y: number) => void;
  onDragEnd?: (x: number, y: number) => void;
  persistPosition?: boolean;
  id?: string;
}
const MobicomMascot: React.FC<MobicomMascotProps> = ({
  width = "auto",
  height,
  top = 350,
  bottom,
  left,
  right = 20,
  zIndex = 1000,
  animate = true,
  animationDuration = 2,
  clickable = true,
  onClick,
  className = "",
  style = {},
  opacity = 1,
  autoPlay = true,
  loop = true,
  muted = true,
  draggable = true,
  constrainToViewport = true,
  onDragStart,
  onDrag,
  onDragEnd,
  persistPosition = false,
  id = "mobicom-mascot",
}) => {
  const theme = useTheme();
  const router = useRouter();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const defaultHeight = height ?? (isMobile ? 80 : 80);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isInitialized, setIsInitialized] = useState(false);

  const [hasMoved, setHasMoved] = useState(false);
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });
  const [trailCircles, setTrailCircles] = useState<TrailCircle[]>([
    { id: 0, targetX: 0, targetY: 0, currentX: 0, currentY: 0 },
    { id: 1, targetX: 0, targetY: 0, currentX: 0, currentY: 0 },
    { id: 2, targetX: 0, targetY: 0, currentX: 0, currentY: 0 },
  ]);
  const animationFrameRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (isInitialized) return;

    if (persistPosition && id) {
      const savedPosition = localStorage.getItem(`mascot-position-${id}`);
      if (savedPosition) {
        const { x, y } = JSON.parse(savedPosition);
        setPosition({ x, y });
        setIsInitialized(true);
        return;
      }
    }

    const calculateInitialPosition = () => {
      if (!videoRef.current) return;

      const rect = videoRef.current.getBoundingClientRect();

      const mascotWidth =
        rect.width > 0
          ? rect.width
          : typeof defaultHeight === "number"
          ? defaultHeight * 1.5
          : 120;
      const mascotHeight =
        rect.height > 0
          ? rect.height
          : typeof defaultHeight === "number"
          ? defaultHeight
          : 80;

      let initialX = 0;
      let initialY = 0;

      if (left !== undefined) {
        initialX = typeof left === "number" ? left : parseFloat(left);
      } else if (right !== undefined) {
        const rightValue =
          typeof right === "number" ? right : parseFloat(right);
        initialX = window.innerWidth - rightValue - mascotWidth;
      }

      if (top !== undefined) {
        initialY = typeof top === "number" ? top : parseFloat(top);
      } else if (bottom !== undefined) {
        const bottomValue =
          typeof bottom === "number" ? bottom : parseFloat(bottom);
        initialY = window.innerHeight - bottomValue - mascotHeight;
      }
      setPosition({ x: initialX, y: initialY });
      setIsInitialized(true);
    };

    const tryCalculate = () => {
      if (videoRef.current) {
        const rect = videoRef.current.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          calculateInitialPosition();
        } else {
          setTimeout(tryCalculate, 50);
        }
      }
    };

    const timer = setTimeout(tryCalculate, 100);
    return () => clearTimeout(timer);
  }, [
    top,
    bottom,
    left,
    right,
    isInitialized,
    persistPosition,
    id,
    defaultHeight,
  ]);

  useEffect(() => {
    const handleResize = () => {
      if (!isDragging && !persistPosition && videoRef.current) {
        const rect = videoRef.current.getBoundingClientRect();
        const mascotWidth = rect.width;
        const mascotHeight = rect.height;

        let newX = position.x;
        let newY = position.y;

        if (right !== undefined) {
          const rightValue =
            typeof right === "number" ? right : parseFloat(right);
          newX = window.innerWidth - rightValue - mascotWidth;
        }

        if (bottom !== undefined) {
          const bottomValue =
            typeof bottom === "number" ? bottom : parseFloat(bottom);
          newY = window.innerHeight - bottomValue - mascotHeight;
        }

        newX = Math.max(0, Math.min(newX, window.innerWidth - mascotWidth));
        newY = Math.max(0, Math.min(newY, window.innerHeight - mascotHeight));
        setPosition({ x: newX, y: newY });
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isDragging, persistPosition, right, bottom, position.x, position.y]);

  const handleDragStart = (clientX: number, clientY: number) => {
    if (!draggable || !videoRef.current) return;

    setIsDragging(true);
    setHasMoved(false);
    setStartPosition({ x: clientX, y: clientY });

    const rect = videoRef.current.getBoundingClientRect();
    setDragOffset({
      x: clientX - rect.left,
      y: clientY - rect.top,
    });

    if (videoRef.current) {
      const centerX = position.x + rect.width / 2;
      const centerY = position.y + rect.height / 2;
      setTrailCircles([
        {
          id: 0,
          targetX: centerX,
          targetY: centerY,
          currentX: centerX,
          currentY: centerY,
        },
        {
          id: 1,
          targetX: centerX,
          targetY: centerY,
          currentX: centerX,
          currentY: centerY,
        },
        {
          id: 2,
          targetX: centerX,
          targetY: centerY,
          currentX: centerX,
          currentY: centerY,
        },
      ]);
    }
    onDragStart?.();
  };

  const handleDragMove = (clientX: number, clientY: number) => {
    if (!isDragging || !videoRef.current) return;

    const deltaX = Math.abs(clientX - startPosition.x);
    const deltaY = Math.abs(clientY - startPosition.y);

    if (deltaX > 5 || deltaY > 5) {
      setHasMoved(true);
    }

    let newX = clientX - dragOffset.x;
    let newY = clientY - dragOffset.y;

    if (constrainToViewport) {
      const rect = videoRef.current.getBoundingClientRect();
      const maxX = window.innerWidth - rect.width;
      const maxY = window.innerHeight - rect.height;

      newX = Math.max(0, Math.min(newX, maxX));
      newY = Math.max(0, Math.min(newY, maxY));
    }
    setPosition({ x: newX, y: newY });
    onDrag?.(newX, newY);
  };

  const handleDragEnd = () => {
    if (!isDragging) return;

    setIsDragging(false);
    if (persistPosition && id) {
      localStorage.setItem(`mascot-position-${id}`, JSON.stringify(position));
    }

    onDragEnd?.(position.x, position.y);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    handleDragStart(e.clientX, e.clientY);
  };

  const handleMouseMove = (e: MouseEvent) => {
    handleDragMove(e.clientX, e.clientY);
  };

  const handleMouseUp = () => {
    handleDragEnd();
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    handleDragStart(touch.clientX, touch.clientY);
  };

  const handleTouchMove = (e: TouchEvent) => {
    e.preventDefault();
    const touch = e.touches[0];
    handleDragMove(touch.clientX, touch.clientY);
  };

  const handleTouchEnd = () => {
    handleDragEnd();
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      window.addEventListener("touchmove", handleTouchMove, { passive: false });
      window.addEventListener("touchend", handleTouchEnd);

      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
        window.removeEventListener("touchmove", handleTouchMove);
        window.removeEventListener("touchend", handleTouchEnd);
      };
    }
  }, [isDragging, dragOffset, position]);

  useEffect(() => {
    if (!isDragging || !videoRef.current) return;

    const animate = () => {
      if (!videoRef.current) return;

      const rect = videoRef.current.getBoundingClientRect();
      const centerX = position.x + rect.width / 2;
      const centerY = position.y + rect.height / 2;

      setTrailCircles((prev) => {
        const newCircles = [...prev];
        newCircles[0] = {
          ...newCircles[0],
          targetX: centerX,
          targetY: centerY,
          currentX:
            newCircles[0].currentX + (centerX - newCircles[0].currentX) * 0.15,
          currentY:
            newCircles[0].currentY + (centerY - newCircles[0].currentY) * 0.15,
        };
        newCircles[1] = {
          ...newCircles[1],
          targetX: newCircles[0].currentX,
          targetY: newCircles[0].currentY,
          currentX:
            newCircles[1].currentX +
            (newCircles[0].currentX - newCircles[1].currentX) * 0.15,
          currentY:
            newCircles[1].currentY +
            (newCircles[0].currentY - newCircles[1].currentY) * 0.15,
        };
        newCircles[2] = {
          ...newCircles[2],
          targetX: newCircles[1].currentX,
          targetY: newCircles[1].currentY,
          currentX:
            newCircles[2].currentX +
            (newCircles[1].currentX - newCircles[2].currentX) * 0.15,
          currentY:
            newCircles[2].currentY +
            (newCircles[1].currentY - newCircles[2].currentY) * 0.15,
        };

        return newCircles;
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isDragging, position]);

  const mascotStyle: CSSProperties = {
    position: "fixed",
    width,
    height: defaultHeight,
    zIndex,
    opacity,
    cursor: draggable
      ? isDragging
        ? "grabbing"
        : "grab"
      : clickable
      ? "pointer"
      : "default",

    animation:
      !isDragging && animate
        ? `mascot-bounce ${animationDuration}s ease-in-out infinite`
        : "none",

    transform: `translate(${position.x}px, ${position.y}px)`,
    transition: isDragging ? "none" : "transform 0.2s ease-out",
    userSelect: "none",
    WebkitUserSelect: "none",
    touchAction: "none",
    ...style,
  };


  const handleClick = () => {
    if (!hasMoved) {
      if (onClick) {
        onClick();
      } else {
        router.push("/promotion");
      }
    }
  };

  return (
    <>
      {isDragging
        ? trailCircles.map((circle, index) => {
            const size = 40 - index * 10;
            const opacity = 0.6 - index * 0.15;
            return (
              <div
                key={circle.id}
                style={{
                  position: "fixed",
                  left: circle.currentX,
                  top: circle.currentY,
                  width: size,
                  height: size,
                  borderRadius: "50%",
                  backgroundColor: "#FF6B9D",
                  transform: `translate(-50%, -50%)`,
                  opacity: opacity,
                  pointerEvents: "none",
                  zIndex: zIndex - 1,
                }}
              />
            );
          })
        : null}
      <video
        ref={videoRef}
        autoPlay={autoPlay}
        loop={loop}
        muted={muted}
        playsInline
        className={`${className}`}
        draggable={false}
        style={mascotStyle}
        onClick={handleClick}
        onMouseDown={draggable ? handleMouseDown : undefined}
        onTouchStart={draggable ? handleTouchStart : undefined}
      >
        <source src="/mascot/mascot-saf.mov" type='video/mp4; codecs="hvc1"' />
        <source src="/mascot/Mobicom mascot.webm" type="video/webm" />
        Your browser does not support the video tag.
      </video>
      {animate && !isDragging && (
        <style jsx>{`
          @keyframes mascot-bounce {
            0%,
            100% {
              transform: translate(${position.x}px, ${position.y}px);
            }
            50% {
              transform: translate(${position.x}px, ${position.y - 20}px);
            }
          }
        `}</style>
      )}
    </>
  );
};

export default MobicomMascot;
